/**
 * @file src/views/VID/views/VidSessionConfigurationError.tsx
 * @exports VidSessionConfigurationError
 * @summary Full-page error when vID web cannot derive a session storage key (missing invite parameters and session storage). Supports high-contrast theme.
 * @remarks Shown by VidSessionKeyGuard; reports to Sentry once per mount.
 */

import { colors, VID, Warning } from "@cerebruminc/cerebellum";
import { useLocation, useSearch } from "@tanstack/react-router";
import * as Sentry from "@sentry/react";
import Head from "src/lib/head";
import { useEffect, useRef } from "react";
import styled from "styled-components";

import { useAtomValue } from "jotai";

import { VidButtonBank } from "src/components/VidButtonBank";
import { highContrastThemeAtom } from "src/shame/shame-atoms";
import {
	CenterBoxWrapper,
	IconBadgeOverlay,
	MessageBodyText,
	MessageCenterBox,
	MessageContainer,
	MessageHeader,
	MessageIconBox,
	MessageIconWrapper,
	VidCenterBase,
} from "./VidStyles";

const SkinnyIconBadgeOverlay = styled(IconBadgeOverlay)`
	&::before {
		width: 8px;
	}
`;

export const VidSessionConfigurationError = () => {
	const isHighContrast = useAtomValue(highContrastThemeAtom);
	const location = useLocation();
	const search = useSearch({ strict: false }) as Record<string, string | string[] | undefined>;
	const didReport = useRef(false);

	useEffect(() => {
		if (didReport.current) {
			return;
		}
		didReport.current = true;
		Sentry.captureMessage("vID web session storage key could not be derived", {
			extra: {
				asPath: location.href,
				pathname: location.pathname,
				query: search,
			},
			level: "error",
			tags: { feature: "vid-web", reason: "missing_vid_storage_key" },
		});
	}, []);

	return (
		<>
			<Head>
				<title>vID: Session error</title>
				<meta name="description" content="Identity verification software." />
			</Head>
			<main>
				<VidCenterBase>
					<CenterBoxWrapper>
						<MessageCenterBox>
							<MessageContainer>
								<MessageIconWrapper>
									<MessageIconBox $highContrast={isHighContrast}>
										<VID fill={colors.WHITE} />
									</MessageIconBox>
									<SkinnyIconBadgeOverlay>
										<Warning fill={colors.RED_100} />
									</SkinnyIconBadgeOverlay>
								</MessageIconWrapper>
								<MessageHeader data-sentry-unmask>We can&apos;t start verification</MessageHeader>
								<MessageBodyText data-sentry-unmask>
									This page needs a valid verification link. Open the link from your invite email or QR code, or contact
									the organization that sent you here if you keep seeing this message.
								</MessageBodyText>
							</MessageContainer>
						</MessageCenterBox>
					</CenterBoxWrapper>
				</VidCenterBase>
				<VidButtonBank />
			</main>
		</>
	);
};
